import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { SharedDataService } from '../shared-service/shared-data.service';
import { urls } from 'src/app/utils/url-utils';
import { errorHandl } from 'src/app/utils/network-utils';
import { sweetAlert } from '../../utils/swal';

@Injectable({
  providedIn: 'root'
})
export class PlacementAuthService {
  httpPlacement: HttpClient;
  public baseUrl: string = '';

  cachedToken: string = '';
  tokenExpires: string = '';
  refreshToken: string = '';
  refreshExpires: string = '';

  currentUserId: string = '';
  activeAuth$: Observable<string> | null = null;
  private isAlertShowing = false;

  showErrorAlert(err: any) {
    if (this.isAlertShowing) {
      return;
    }
    this.isAlertShowing = true;
    sweetAlert("Error", err, "warning", "OK").then(() => {
      this.isAlertShowing = false;
    });
  }

  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private sharedDataService: SharedDataService
  ) {
    this.httpPlacement = new HttpClient(handler);
    this.loadTokensFromStorage();
  }

  loadTokensFromStorage(): void {
    try {
      this.cachedToken = localStorage.getItem('placement_access_token') || '';
      this.tokenExpires = localStorage.getItem('placement_access_expiry') || '';
      this.refreshToken = localStorage.getItem('placement_refresh_token') || '';
      this.refreshExpires = localStorage.getItem('placement_refresh_expiry') || '';
      this.baseUrl = localStorage.getItem('placement_base_url') || '';
    } catch (e) {
      console.error('Error loading tokens from localStorage', e);
    }
  }

  saveTokensToStorage(): void {
    try {
      localStorage.setItem('placement_access_token', this.cachedToken);
      localStorage.setItem('placement_access_expiry', this.tokenExpires);
      localStorage.setItem('placement_refresh_token', this.refreshToken);
      localStorage.setItem('placement_refresh_expiry', this.refreshExpires);
      localStorage.setItem('placement_base_url', this.baseUrl);
    } catch (e) {
      console.error('Error saving tokens to localStorage', e);
    }
  }

  clearTokensFromStorage(): void {
    try {
      this.cachedToken = '';
      this.tokenExpires = '';
      this.refreshToken = '';
      this.refreshExpires = '';
      this.baseUrl = '';
      this.currentUserId = '';
      localStorage.removeItem('placement_access_token');
      localStorage.removeItem('placement_access_expiry');
      localStorage.removeItem('placement_refresh_token');
      localStorage.removeItem('placement_refresh_expiry');
      localStorage.removeItem('placement_base_url');
    } catch (e) {
      console.error('Error clearing tokens from localStorage', e);
    }
  }

   isAccessTokenValid(): boolean {
    if (!this.cachedToken) {
      return false;
    }
    if (this.tokenExpires) {
      const expiry = new Date(this.tokenExpires);
      if (isNaN(expiry.getTime())) {
        return false;
      }
      const now = new Date();
      if (expiry.getTime() - now.getTime() < 60000) {
        return false;
      }
    }
    return true;
  }

  isRefreshTokenValid(): boolean {
    if (!this.refreshToken) {
      return false;
    }
    if (this.refreshExpires) {
      const expiry = new Date(this.refreshExpires);
      if (isNaN(expiry.getTime())) {
        return false;
      }
      const now = new Date();
      if (expiry.getTime() - now.getTime() < 60000) {
        return false;
      }
    }
    return true;
  }

  login(user?: any): Observable<string> {
    const user$ = user ? of(user) : this.sharedDataService.getUser().pipe(
      filter(u => {
        if (!localStorage.getItem('token')) {
          return true;
        }
        return u !== null;
      }),
      take(1)
    );

    return user$.pipe(
      switchMap(u => {
        if (!u) {
          this.clearTokensFromStorage();
          this.currentUserId = '';
          return of('');
        }

        if (u.allowPlacement !== true) {
          this.clearTokensFromStorage();
          this.currentUserId = '';
          return of('');
        }

        // 1. Check in-memory session first. If user is same and token is valid, reuse it instantly.
        if (this.currentUserId === u._id && this.isAccessTokenValid()) {
          return of(this.cachedToken);
        }

        // 2. Otherwise (token expired, or user context changed/initialized), fetch configs to verify/login.
        return this.http.get<any[]>(urls.BASE_URL + urls.FS_CONFIGURATIONS).pipe(
          switchMap(configurations => {
            const matchedFs = configurations.find((config: any) => config.cooperate === u._id);
            const expectedBaseUrl = matchedFs ? matchedFs.url : '';

            if (this.baseUrl && this.baseUrl !== expectedBaseUrl) {
              this.clearTokensFromStorage();
            }
            this.baseUrl = expectedBaseUrl;
            this.currentUserId = u._id;

            if (this.isAccessTokenValid()) {
              return of(this.cachedToken);
            }
            return this.authenticate(u, matchedFs);
          })
        );
      }),
      tap({
        error: err => {
          this.showErrorAlert(err);
        }
      })
    );
  }

  authenticate(user: any, matchedFs: any): Observable<string> {
    if (this.activeAuth$) {
      return this.activeAuth$;
    }

    if (this.isRefreshTokenValid()) {
      this.activeAuth$ = this.refreshAccessToken().pipe(
        catchError(err => {
          console.warn('Placement token refresh failed, falling back to full login', err);
          return this.fullLogin(user, matchedFs);
        }),
        finalize(() => {
          this.activeAuth$ = null;
        }),
        shareReplay(1)
      );
    } else {
      this.activeAuth$ = this.fullLogin(user, matchedFs).pipe(
        finalize(() => {
          this.activeAuth$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.activeAuth$;
  }

  refreshAccessToken(): Observable<string> {
    if (!this.refreshToken) {
      return throwError('No refresh token available');
    }
    const payload = { refreshToken: this.refreshToken };
    return this.httpPlacement.post<any>(`${this.baseUrl}/auth/refresh-tokens`, payload).pipe(
      map(res => {
        const access = res && (res.access || (res.tokens && res.tokens.access) || res);
        const refresh = res && (res.refresh || (res.tokens && res.tokens.refresh));
        if (access && access.token) {
          this.cachedToken = access.token;
          this.tokenExpires = access.expires || '';
          if (refresh && refresh.token) {
            this.refreshToken = refresh.token;
            this.refreshExpires = refresh.expires || '';
          }
          this.saveTokensToStorage();
          return this.cachedToken;
        }
        throw new Error('Refresh token API did not return an access token');
      })
    );
  }

  fullLogin(user: any, matchedFs: any): Observable<string> {
    if (!matchedFs) {
      this.clearTokensFromStorage();
      return of('');
    }
    const payload = {
      username: matchedFs.email,
      password: matchedFs.password,
      type: 'responsibleparties'
    };
    return this.httpPlacement.post<any>(`${this.baseUrl}/patients/login`, payload).pipe(
      map(res => {
        if (res && res.tokens) {
          if (res.tokens.access) {
            this.cachedToken = res.tokens.access.token;
            this.tokenExpires = res.tokens.access.expires || '';
          }
          if (res.tokens.refresh) {
            this.refreshToken = res.tokens.refresh.token;
            this.refreshExpires = res.tokens.refresh.expires || '';
          }
          this.saveTokensToStorage();
        }
        return this.cachedToken;
      }),
      catchError(err => {
        this.clearTokensFromStorage();
        if (err && err.error && typeof err.error.error === 'string' && !err.error.message) {
          err.error.message = err.error.error;
        }
        return errorHandl(err);
      })
    );
  }
}



